import React from "react";
import {
  fetchPullPlanWBSTree,
  listPullPlans,
} from "../../../api/clientV2";
import type {
  PullPlanStatus,
  PullPlanSummary,
  PullPlanWBSNode,
} from "../../../models";
import Spinner from "../../../components/Spinner/Spinner";
import PullHistoryRow from "../PullHistoryRow/PullHistoryRow";
import CompletedPlanView from "../CompletedPlanView/CompletedPlanView";
import "./PullPlanHistory.css";

interface Props {
  scheduleId: number;
  /** Called when the user clicks a row. `draftIdToResume` is the pull-plan id
   * when the user clicked a draft (SectionView should open straight into Plan
   * mode with that draft); null for committed rows (SectionView opens in
   * Browse mode as usual). */
  onOpenSection: (wbsNodeId: number, draftIdToResume: number | null) => void;
  /** Bumped when returning from the canvas so a just-published or discarded
   *  draft doesn't linger in the list. */
  refreshNonce?: number;
  /** Resume a Takt-section draft via the same path as "Continue Plan" — sets the
   *  Takt section name so the board wires up its scope, dep graph and level UI.
   *  Plain (non-Takt) drafts fall back to onOpenSection's WBS-anchor path. */
  onResumeTaktDraft?: (taktSectionName: string, draftId: number) => void;
}

type StatusFilter = "all" | Exclude<PullPlanStatus, "discarded">;

const FILTERS: Array<{ id: StatusFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "draft", label: "Drafts" },
  { id: "committed", label: "Committed" },
];

const PullPlanHistory: React.FC<Props> = ({
  scheduleId,
  onOpenSection,
  refreshNonce,
  onResumeTaktDraft,
}) => {
  const [pullPlans, setPullPlans] = React.useState<PullPlanSummary[]>([]);
  const [wbsNodes, setWbsNodes] = React.useState<PullPlanWBSNode[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<StatusFilter>("all");
  // A committed plan is immutable — clicking it opens a read-only view of what
  // it did rather than resuming an editable draft.
  const [viewing, setViewing] = React.useState<PullPlanSummary | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      listPullPlans(scheduleId, {}),
      fetchPullPlanWBSTree(scheduleId),
    ])
      .then(([pps, tree]) => {
        if (cancelled) return;
        setPullPlans(pps.filter((p) => p.status !== "discarded"));
        setWbsNodes(tree);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message ?? "Failed to load pull plan history.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [scheduleId, refreshNonce]);

  const sectionNameById = React.useMemo(() => {
    const m = new Map<number, string>();
    for (const n of wbsNodes) m.set(n.id, n.name);
    return m;
  }, [wbsNodes]);

  const filtered = React.useMemo(() => {
    if (filter === "all") return pullPlans;
    return pullPlans.filter((p) => p.status === filter);
  }, [pullPlans, filter]);

  const counts = React.useMemo(() => {
    const out: Record<StatusFilter, number> = {
      all: pullPlans.length,
      draft: 0, committed: 0,
    };
    for (const p of pullPlans) {
      if (p.status === "draft" || p.status === "committed") out[p.status] += 1;
    }
    return out;
  }, [pullPlans]);

  return (
    <div className="pull-plan-history">
      <header className="pull-plan-history-header">
        <h2>Pull History</h2>
        <div className="pull-plan-history-filters" role="tablist">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={`pull-plan-history-filter${filter === f.id ? " is-active" : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              <span className="pull-plan-history-filter-count">{counts[f.id]}</span>
            </button>
          ))}
        </div>
      </header>

      {error && <div className="pull-plan-error">{error}</div>}
      <Spinner visible={loading} label="Loading pull history..." />

      {!loading && filtered.length === 0 && (
        <div className="pull-plan-history-empty">
          {pullPlans.length === 0
            ? "No pulls yet. Start one from the Sections tab."
            : `No ${filter} entries.`}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="pull-plan-history-list" role="list">
          {filtered.map((pp) => (
            <PullHistoryRow
              key={pp.id}
              pullPlan={pp}
              sectionLabel={sectionNameById.get(pp.section_wbs_id) ?? "—"}
              onClick={() => {
                if (pp.status !== "draft") {
                  setViewing(pp);
                } else if (pp.takt_section_name && onResumeTaktDraft) {
                  onResumeTaktDraft(pp.takt_section_name, pp.id);
                } else {
                  onOpenSection(pp.section_wbs_id, pp.id);
                }
              }}
              variant="tab"
            />
          ))}
        </div>
      )}

      {viewing && (
        <CompletedPlanView
          scheduleId={scheduleId}
          plan={viewing}
          sectionLabel={sectionNameById.get(viewing.section_wbs_id) ?? "—"}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
};

export default PullPlanHistory;
